import React, { useState } from 'react';
import { Modal, Table, Tag, Tooltip, Button } from 'antd';
import DeloitteLoader from '../../../components/ui/DeloitteLoader/DeloitteLoader';
import LifeSciencesRagService, { ILsRagMessage } from '../../../libs/services/LifeSciencesRagService/LifeSciencesRagService';
import { RoutingMode, IEstimateQueryResponse } from '../../../libs/models/GenAIModels';
import { _skillsEngineEstimate } from './CopilotAgentsHelpers';

interface ICostEstimatorProps {
  input: string;
  disableCache: boolean;
  routingMode: RoutingMode;
  selectedModel: string;
  // null for the RAG agent (ls-rag's own /ls-rag/estimate, sized by
  // retrieved context) — a Skills Engine agent_id for everyone else
  // (/api/chat/estimate, agent-aware tiered routing).
  agentId: string | null;
  history: ILsRagMessage[];
}

const tierColumns = [
  { title: 'Tier', dataIndex: 'tier', key: 'tier', render: (t: string) => t.replace('_', ' ') },
  { title: 'Model', dataIndex: 'model', key: 'model' },
  { title: 'Est. Input Tokens', dataIndex: 'estimatedInputTokens', key: 'estimatedInputTokens' },
  { title: 'Est. Output Tokens', dataIndex: 'estimatedOutputTokens', key: 'estimatedOutputTokens' },
  {
    title: 'Est. Cost',
    dataIndex: 'estimatedCost',
    key: 'estimatedCost',
    render: (cost: number) => `$${cost.toFixed(6)}`,
  },
  {
    title: 'Basis',
    dataIndex: 'estimateBasis',
    key: 'estimateBasis',
    render: (basis: string, row: any) =>
      basis === 'learned' ? (
        <Tag color="gold" title={`Averaged from ${row.sampleSize} past runs on this tier`}>
          {`learned (${row.sampleSize})`}
        </Tag>
      ) : (
        <Tag title="Not enough past runs yet — using a default output/input ratio">default</Tag>
      ),
  },
];

// The "$" cost estimator for the Agents tab — same pre-send tier/cost
// breakdown Copilot Queries' SearchBox already shows, against whichever
// endpoint fits the active agent: ls-rag's own /ls-rag/estimate (sized by
// retrieved context, not just the question text) or the generic
// /api/chat/estimate for every other agent. Self-contained (owns its own
// fetch/modal state) so CopilotAgents.tsx only needs to render
// <CostEstimator ... /> for it.
const CostEstimator: React.FC<ICostEstimatorProps> = ({ input, disableCache, routingMode, selectedModel, agentId, history }) => {
  const [queryEstimate, setQueryEstimate] = useState<IEstimateQueryResponse | null>(null);
  const [estimatedFor, setEstimatedFor] = useState('');
  const [estimating, setEstimating] = useState(false);
  const [isEstimateModalOpen, setIsEstimateModalOpen] = useState(false);

  const ensureEstimate = () => {
    if (!input || estimatedFor === input) return;
    setEstimatedFor(input);
    setEstimating(true);
    setQueryEstimate(null);
    const manualModel = routingMode === 'manual' ? selectedModel : undefined;
    const estimate = agentId
      ? _skillsEngineEstimate(agentId, input, history, disableCache, manualModel, routingMode)
      : LifeSciencesRagService.estimateQuery(input, disableCache, manualModel, routingMode);
    estimate
      .then(res => setQueryEstimate(res))
      .catch(() => setQueryEstimate(null))
      .finally(() => setEstimating(false));
  };

  const handleEstimateClick = () => {
    ensureEstimate();
    setIsEstimateModalOpen(true);
  };

  const handleCloseEstimate = () => setIsEstimateModalOpen(false);

  const recommendedTierEstimate = queryEstimate?.tiers.find(t => t.tier === queryEstimate.recommendedTier);
  const estimateTooltipTitle = !input ? (
    'Type a query to see its estimated cost'
  ) : estimating && estimatedFor === input ? (
    'Estimating cost…'
  ) : recommendedTierEstimate ? (
    <>
      <div>{`Recommended (${recommendedTierEstimate.tier.replace('_', ' ')}): $${recommendedTierEstimate.estimatedCost.toFixed(6)}`}</div>
      <div>{'Click for full breakdown'}</div>
    </>
  ) : (
    'Click to estimate cost'
  );

  return (
    <>
      {/* estimate-cost-button-wrap: same wrapper + Ant Button shape="circle" pattern
          as SearchBox — keeps a stable 38px flex item regardless of whether Ant's
          Tooltip internally wraps the disabled child in a span, and lets the scoped
          .ant-btn CSS below apply reliably via class instead of the :disabled selector
          (which Ant can move off the button onto the wrapper span). */}
      <span className="cop-inp-row__estimate-wrap">
        <Tooltip title={estimateTooltipTitle} onVisibleChange={visible => visible && ensureEstimate()}>
          <Button
            shape="circle"
            onClick={handleEstimateClick}
            disabled={!input.trim()}
            aria-label="Estimate cost"
          >
            {'$'}
          </Button>
        </Tooltip>
      </span>

      <Modal
        title="Estimated cost for this query"
        open={isEstimateModalOpen}
        onCancel={handleCloseEstimate}
        className="estimate-cost-modal"
        width={680}
        destroyOnClose
        footer={[
          <Button key="close" type="primary" onClick={handleCloseEstimate}>
            {'Close'}
          </Button>,
        ]}
      >
        {estimating ? (
          <div className="estimate-cost-modal__loading">
            <DeloitteLoader size={32} style={{ display: 'block', margin: 'auto' }} />
          </div>
        ) : (
          <>
            {queryEstimate?.cacheHit && (
              <div className="estimate-cost-modal__banner estimate-cost-modal__banner--cache">
                {`This exact query was answered before by ${queryEstimate.cachedModel} — it will be served from cache at no cost.`}
              </div>
            )}
            {routingMode === 'auto' ? (
              <div className="estimate-cost-modal__banner estimate-cost-modal__banner--auto">
                {`Auto mode will route to: ${queryEstimate?.recommendedTier?.replace('_', ' ') ?? '…'}`}
              </div>
            ) : (
              <div className="estimate-cost-modal__banner estimate-cost-modal__banner--manual">
                {`Manual model selected: ${selectedModel}`}
              </div>
            )}
            <Table
              size="small"
              pagination={false}
              rowKey="tier"
              columns={tierColumns}
              dataSource={(queryEstimate?.tiers as any[]) || []}
              rowClassName={(row: any) =>
                row.tier === queryEstimate?.recommendedTier ? 'estimate-cost-modal__row--recommended' : ''
              }
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default CostEstimator;
